import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { trackLogin, trackSignUp, setUserId, setUserProperties } from "@/utils/googleAnalytics";
import { invalidateMarketConfig } from "@/utils/marketConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_REQUEST_TIMEOUT = 15000;
const SESSION_LINK_TIMEOUT = 5000;
const AuthContext = createContext(null);

// RFC-4122 v4 UUID. The backend now validates X-Session-ID is a real UUID
// (to blunt session-id spoofing), so the non-crypto fallback must also emit
// UUID format — not the old base36 string.
function uuidv4() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateSessionId() {
  let sid = localStorage.getItem("pos_session");
  // Migrate any legacy non-UUID session id so it passes backend validation.
  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!sid || !uuidRe.test(sid)) {
    sid = uuidv4();
    localStorage.setItem("pos_session", sid);
  }
  return sid;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pos_token"));
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(getOrCreateSessionId);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}`, "X-Session-ID": sessionId }, timeout: AUTH_REQUEST_TIMEOUT })
        .then(res => {
          setUser(res.data);
          // Link session data to user after login
          axios.post(`${API}/session/link`, {}, { headers: { Authorization: `Bearer ${token}`, "X-Session-ID": sessionId }, timeout: SESSION_LINK_TIMEOUT }).catch(() => {});
        })
        .catch(() => { localStorage.removeItem("pos_token"); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, sessionId]);

  const login = async (email, password, accountType = "candidate") => {
    const res = await axios.post(
      `${API}/auth/login`,
      { email, password, account_type: accountType },
      { headers: { "X-Session-ID": sessionId }, timeout: AUTH_REQUEST_TIMEOUT }
    );
    localStorage.setItem("pos_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    // Market may change identity: logged-in users see their ACCOUNT market
    // (pinned server-side at this login if it wasn't already), not live geo.
    invalidateMarketConfig();

    // Track login in GA4
    trackLogin('email');
    setUserId(res.data.user.id);
    setUserProperties({
      user_email: email,
      user_name: res.data.user.name || '',
    });
    
    // Link session
    // Session linking is a non-critical background sweep. Do not block a
    // successful sign-in on Mongo/analytics work or a temporarily slow store.
    axios.post(`${API}/session/link`, {}, { headers: { Authorization: `Bearer ${res.data.token}`, "X-Session-ID": sessionId }, timeout: SESSION_LINK_TIMEOUT }).catch(() => {});
    return res.data;
  };

  const register = async (email, password, name, recruiterDetails = {}) => {
    const res = await axios.post(
      `${API}/auth/register`,
      { email, password, name, ...recruiterDetails },
      { headers: { "X-Session-ID": sessionId }, timeout: AUTH_REQUEST_TIMEOUT }
    );
    localStorage.setItem("pos_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    // New account got its market stamped at signup — switch display onto it.
    invalidateMarketConfig();

    // Track sign up in GA4
    trackSignUp('email');
    setUserId(res.data.user.id);
    setUserProperties({
      user_email: email,
      user_name: name || '',
    });
    
    // Link session
    axios.post(`${API}/session/link`, {}, { headers: { Authorization: `Bearer ${res.data.token}`, "X-Session-ID": sessionId }, timeout: SESSION_LINK_TIMEOUT }).catch(() => {});
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("pos_token");
    setToken(null);
    setUser(null);
    // Back to the logged-out rule: market follows live IP geo again.
    invalidateMarketConfig();
    // Regenerate the browser session id so the NEXT user in this browser does
    // not inherit this session — a shared pos_session previously let a freshly
    // logged-in account see the prior account's session-scoped records.
    const fresh = uuidv4();
    localStorage.setItem("pos_session", fresh);
    setSessionId(fresh);
  };

  const getAuthHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Headers for ALL requests (includes session ID, optionally auth)
  const getHeaders = useCallback(() => {
    const h = { "X-Session-ID": sessionId };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token, sessionId]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, getHeaders, setUser, sessionId }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
